// P5 · Neutral-rewrite diff. Renders the bundle's PRECOMPUTED diff_ops as tracked
// changes (inline or side-by-side), original always visible. Tapping a changed span
// opens its change_log entry (axis, before/after, why) with grounded-claim chips that
// cross-link into P4 — the popover itself is the shared ChangeDetail (also used by the
// C5 reading-pane reframe trace). Persistent AI disclosure whenever P5 is shown (spec §5).

import type { CSSProperties, Dispatch } from "react";
import type { Bundle, DiffOp } from "../types";
import type { UIState, Patch } from "../state";
import type { Telemetry } from "../telemetry";
import { INK, INK_DEEP, insStyle, delStyle, sectionLabel } from "../style";
import { type OpenSource } from "./SourceLink";
import { ChangeDetail } from "./ChangeDetail";

interface OpView { isEqual: boolean; isChange: boolean; text: string; ref?: number; style?: CSSProperties }

function splitStyled(list: OpView[]): { ops: OpView[]; key: number }[] {
  const out: OpView[][] = [[]];
  list.forEach((op) => {
    if (op.isEqual) {
      const parts = op.text.split("\n\n");
      parts.forEach((pt, idx) => { if (idx > 0) out.push([]); if (pt) out[out.length - 1].push({ isEqual: true, isChange: false, text: pt }); });
    } else {
      out[out.length - 1].push(op);
    }
  });
  return out.filter((p) => p.length).map((o, i) => ({ ops: o, key: i }));
}

export function RewriteDiff({ b, s, set, tel, onOpenSource }: {
  b: Bundle; s: UIState; set: Dispatch<Patch>; tel: Telemetry; onOpenSource?: OpenSource;
}) {
  const rw = b.rewrite;
  const ops: DiffOp[] = rw.diff_ops || [];

  const mapInline = (op: DiffOp): OpView => {
    if (op.op === "equal") return { isEqual: true, isChange: false, text: op.text };
    const active = s.activeChange === op.change_ref;
    return { isEqual: false, isChange: true, text: op.text, ref: op.change_ref, style: op.op === "insert" ? insStyle(active) : delStyle(active) };
  };
  const inlineOps = ops.map(mapInline);
  const origOps = ops.filter((o) => o.op !== "insert").map((op) => op.op === "equal" ? { isEqual: true, isChange: false, text: op.text } : { isEqual: false, isChange: true, text: op.text, ref: op.change_ref, style: delStyle(s.activeChange === op.change_ref) });
  const rewOps = ops.filter((o) => o.op !== "delete").map((op) => op.op === "equal" ? { isEqual: true, isChange: false, text: op.text } : { isEqual: false, isChange: true, text: op.text, ref: op.change_ref, style: insStyle(s.activeChange === op.change_ref) });

  const ch = s.activeChange != null ? rw.change_log[s.activeChange] : null;

  const onDiffOp = (ref: number | undefined, rect: DOMRect) => {
    if (ref == null) return;
    if (s.activeChange === ref) { set({ activeChange: null, groundedClaim: null }); return; }
    tel.log("diff_change_open", { change_index: ref, axis: rw.change_log[ref]?.axis });
    // anchor the rationale popover to the clicked edit (viewport-clamped), like the highlight tooltip
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - 392));
    let top = rect.bottom + 8; if (top > window.innerHeight - 300) top = Math.max(12, rect.top - 300);
    set({ activeChange: ref, groundedClaim: null, tipPos: { top, left }, pinnedHl: null, reframeChangeOpen: null });
  };

  const renderParas = (views: OpView[]) => splitStyled(views).map((para) => (
    <p key={para.key} style={{ margin: "0 0 14px" }}>
      {para.ops.map((op, i) => {
        if (op.isEqual) return <span key={i}>{op.text}</span>;
        // No change_log entry to open — keep the tracked-change visuals but render a
        // non-interactive span (no pointer affordance, no dead click). Only spans that
        // resolve a change_ref stay clickable buttons that open the popover.
        if (op.ref == null) return <span key={i} style={{ ...op.style, cursor: "default" }}>{op.text}</span>;
        return <button key={i} onClick={(e) => onDiffOp(op.ref, e.currentTarget.getBoundingClientRect())} style={op.style}>{op.text}</button>;
      })}
    </p>
  ));

  return (
    <section style={{ marginTop: 20, background: "#fff", border: "1px solid rgba(55,53,47,.12)", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ padding: "14px 22px", background: "#f7f6f3", borderBottom: "1px solid rgba(55,53,47,.09)", display: "flex", alignItems: "center", gap: 11 }}>
        <span style={{ width: 24, height: 24, borderRadius: 5, background: INK, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flex: "none" }}>⇄</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>Neutralized rewrite, shown as tracked changes</div>
          <div style={{ fontSize: 11.5, color: "rgba(55,53,47,.55)" }}>Original wording stays visible. This is a diff, not a replacement.</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 22px", background: "#fbfbfa", borderBottom: "1px solid rgba(55,53,47,.08)" }}>
        <span style={{ flex: "none", marginTop: 1, fontSize: 12, color: "rgba(55,53,47,.5)" }}>ⓘ</span>
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "rgba(55,53,47,.6)" }}>{b.disclosure}</p>
      </div>

      {rw.no_op ? (
        <div style={{ padding: "30px 22px", textAlign: "center", color: "rgba(55,53,47,.6)", fontSize: 14 }}>
          This article was already close to neutral; no changes were made.
        </div>
      ) : (
        <div style={{ padding: "22px 24px 26px" }}>
          {/* headline before/after */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ ...sectionLabel, letterSpacing: ".07em", marginBottom: 5 }}>Headline</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 17, fontWeight: 600, lineHeight: 1.3, color: "rgba(55,53,47,.42)", textDecoration: "line-through", textDecorationColor: "rgba(55,53,47,.32)", marginBottom: 3 }}>{b.target.headline}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 17, lineHeight: 1.3, color: INK_DEEP, fontWeight: 700 }}>{rw.rewritten_headline}</div>
          </div>

          {s.diffStyle === "inline" ? (
            <div style={{ fontSize: 16, lineHeight: 1.78, color: INK }}>{renderParas(inlineOps)}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ ...sectionLabel, letterSpacing: ".07em", color: "rgba(55,53,47,.45)", marginBottom: 7 }}>As published</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.7, color: INK }}>{renderParas(origOps)}</div>
              </div>
              <div>
                <div style={{ ...sectionLabel, letterSpacing: ".07em", color: "#176c3a", fontWeight: 700, marginBottom: 7 }}>Neutralized</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.7, color: INK }}>{renderParas(rewOps)}</div>
              </div>
            </div>
          )}

          {/* change detail — shared floating popover anchored to the clicked edit (mirrors the P3 highlight tooltip) */}
          {ch && (
            <ChangeDetail ch={ch} b={b} tipPos={s.tipPos}
              onClose={() => set({ activeChange: null, groundedClaim: null })}
              groundedClaim={s.groundedClaim} setGroundedClaim={(id) => set({ groundedClaim: id })}
              tel={tel} onOpenSource={onOpenSource} fromChange={s.activeChange} />
          )}
        </div>
      )}
    </section>
  );
}
